import React, { useState, useEffect, useCallback, useRef } from 'react';
import { reportService } from '../../services/reportService';
import { userPreferencesService } from '../../services/api';
import DateRangeFilter from './DateRangeFilter';
import ColumnPicker from './ColumnPicker';
import { Download, AlertTriangle, FileText, X, SlidersHorizontal, Save, ChevronDown, Trash2 } from 'lucide-react';
import SortableHeader, { useSort } from './SortableHeader';
import UserSelectDropdown from '../common/UserSelectDropdown';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'intake', label: 'Intake' },
  { value: 'pending', label: 'Pending' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'closed', label: 'Closed' },
];

const ALL_COLUMNS = [
  { key: 'case_number', label: 'Case #', default: true, width: 'medium' },
  { key: 'title', label: 'Title', default: false, width: 'wide' },
  { key: 'client_name', label: 'Client', default: true, width: 'medium' },
  { key: 'attorney_name', label: 'Attorney', default: true, width: 'medium' },
  { key: 'case_type', label: 'Type', default: true, width: 'narrow' },
  { key: 'status', label: 'Status', default: true, width: 'narrow' },
  { key: 'intake_status_name', label: 'Intake Sub-Status', default: false, width: 'narrow' },
  { key: 'opened_date', label: 'Opened', default: true, width: 'narrow' },
  { key: 'closed_date', label: 'Closed', default: false, width: 'narrow' },
  { key: 'created_at', label: 'Created', default: false, width: 'narrow' },
  { key: 'description', label: 'Description', default: false, width: 'wide' },
  { key: 'billing_type', label: 'Billing Type', default: false, width: 'narrow' },
  { key: 'total_estimated_value', label: 'Est. Value', default: false, width: 'narrow' },
  { key: 'flat_fee_amount', label: 'Flat Fee', default: false, width: 'narrow' },
  { key: 'contingency_percentage', label: 'Contingency %', default: false, width: 'narrow' },
  { key: 'client_email', label: 'Client Email', default: false, width: 'medium' },
  { key: 'client_phone', label: 'Client Phone', default: false, width: 'narrow' },
  { key: 'client_type', label: 'Client Type', default: false, width: 'narrow' },
  { key: 'requires_trust_funds', label: 'Requires Trust', default: false, width: 'narrow' },
];

const DEFAULT_COLUMNS = ALL_COLUMNS.filter(c => c.default).map(c => c.key);

const statusColors = {
  active: 'bg-green-100 text-green-800',
  intake: 'bg-blue-100 text-blue-800',
  closed: 'bg-gray-100 text-gray-800',
  pending: 'bg-yellow-100 text-yellow-800',
  on_hold: 'bg-orange-100 text-orange-800',
};

function formatCellValue(key, value) {
  if (value == null || value === '') return '-';
  switch (key) {
    case 'opened_date':
    case 'closed_date':
    case 'created_at':
      return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    case 'total_estimated_value':
    case 'flat_fee_amount':
      return `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case 'contingency_percentage':
      return `${Number(value)}%`;
    case 'requires_trust_funds':
      return value ? 'Yes' : 'No';
    case 'billing_type':
      return value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    case 'case_type':
      return value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    default:
      return String(value);
  }
}

function renderCell(key, item) {
  const value = item[key];
  if (key === 'case_number') {
    return (
      <div>
        <div className="font-medium text-gray-900">{item.case_number}</div>
        <div className="text-xs text-gray-500">{item.title}</div>
      </div>
    );
  }
  if (key === 'status') {
    return (
      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[value] || 'bg-gray-100 text-gray-800'}`}>
        {value}
      </span>
    );
  }
  return <span className="text-gray-700">{formatCellValue(key, value)}</span>;
}

function generateId() {
  return 'xxxxxxxx-xxxx-4xxx'.replace(/[x]/g, () => ((Math.random() * 16) | 0).toString(16));
}

const CaseListReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedAttorneys, setSelectedAttorneys] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [caseTypeFilter, setCaseTypeFilter] = useState('');

  // Column state
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_COLUMNS);
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  // Saved configs state
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [activeConfigId, setActiveConfigId] = useState(null);
  const [showConfigDropdown, setShowConfigDropdown] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveConfigName, setSaveConfigName] = useState('');
  const [configsLoaded, setConfigsLoaded] = useState(false);
  const configDropdownRef = useRef(null);

  // Dropdown data
  const [caseTypes, setCaseTypes] = useState([]);

  // Load saved configs on mount
  useEffect(() => {
    const loadConfigs = async () => {
      try {
        const res = await userPreferencesService.getCaseListReportConfigs();
        const prefData = res.data?.preference_data || {};
        const configs = prefData.configs || [];
        setSavedConfigs(configs);

        // Apply last used config
        const lastId = prefData.last_used_config_id;
        if (lastId) {
          const config = configs.find(c => c.id === lastId);
          if (config) {
            applyConfig(config);
            setActiveConfigId(lastId);
          }
        }
      } catch {
        // No saved configs yet — that's fine
      }
      setConfigsLoaded(true);
    };
    loadConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close config dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (configDropdownRef.current && !configDropdownRef.current.contains(e.target)) {
        setShowConfigDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const applyConfig = (config) => {
    if (config.columns?.length > 0) setVisibleColumns(config.columns);
    const f = config.filters || {};
    setSelectedAttorneys(
      (f.attorney_ids || []).map((id, i) => ({
        id,
        display_name: (f.attorney_names || [])[i] || 'Attorney',
      }))
    );
    setStatusFilter(f.status_filter || '');
    setCaseTypeFilter(f.case_type_filter || '');
    setStartDate(f.start_date || '');
    setEndDate(f.end_date || '');
  };

  const getCurrentConfigState = () => ({
    columns: visibleColumns,
    filters: {
      attorney_ids: selectedAttorneys.map(a => a.id),
      attorney_names: selectedAttorneys.map(a => a.display_name),
      status_filter: statusFilter,
      case_type_filter: caseTypeFilter,
      start_date: startDate,
      end_date: endDate,
    },
  });

  const persistConfigs = async (configs, lastUsedId) => {
    try {
      await userPreferencesService.updateCaseListReportConfigs({
        configs,
        last_used_config_id: lastUsedId || null,
      });
    } catch (err) {
      console.error('Failed to save report configs:', err);
    }
  };

  const handleSaveConfig = async (name) => {
    const state = getCurrentConfigState();
    let updatedConfigs;
    let newId;

    if (activeConfigId) {
      // Update existing config
      updatedConfigs = savedConfigs.map(c =>
        c.id === activeConfigId ? { ...c, ...state, name: name || c.name } : c
      );
      newId = activeConfigId;
    } else {
      // Create new config
      newId = generateId();
      updatedConfigs = [...savedConfigs, { id: newId, name, ...state }];
    }

    setSavedConfigs(updatedConfigs);
    setActiveConfigId(newId);
    await persistConfigs(updatedConfigs, newId);
    setShowSaveDialog(false);
    setSaveConfigName('');
  };

  const handleSaveAsConfig = async (name) => {
    const state = getCurrentConfigState();
    const newId = generateId();
    const updatedConfigs = [...savedConfigs, { id: newId, name, ...state }];
    setSavedConfigs(updatedConfigs);
    setActiveConfigId(newId);
    await persistConfigs(updatedConfigs, newId);
    setShowSaveDialog(false);
    setSaveConfigName('');
  };

  const handleDeleteConfig = async (id) => {
    const updatedConfigs = savedConfigs.filter(c => c.id !== id);
    const newActiveId = activeConfigId === id ? null : activeConfigId;
    setSavedConfigs(updatedConfigs);
    setActiveConfigId(newActiveId);
    if (newActiveId === null) {
      setVisibleColumns(DEFAULT_COLUMNS);
    }
    await persistConfigs(updatedConfigs, newActiveId);
  };

  const handleSelectConfig = async (config) => {
    applyConfig(config);
    setActiveConfigId(config.id);
    setShowConfigDropdown(false);
    await persistConfigs(savedConfigs, config.id);
  };

  const handleSelectDefault = async () => {
    setVisibleColumns(DEFAULT_COLUMNS);
    setActiveConfigId(null);
    setShowConfigDropdown(false);
    await persistConfigs(savedConfigs, null);
  };

  const getFilterParams = useCallback(() => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (selectedAttorneys.length > 0) params.attorney_ids = selectedAttorneys.map(a => a.id).join(',');
    if (statusFilter) params.status_filter = statusFilter;
    if (caseTypeFilter) params.case_type_filter = caseTypeFilter;
    return params;
  }, [startDate, endDate, selectedAttorneys, statusFilter, caseTypeFilter]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await reportService.getCaseList(getFilterParams());
      setData(response.data);

      const types = [...new Set((response.data?.items || []).map(i => i.case_type).filter(Boolean))].sort();
      setCaseTypes(prev => {
        const merged = [...new Set([...prev, ...types])].sort();
        return merged;
      });
    } catch (err) {
      console.error('Error fetching case list:', err);
      setError('Failed to load Case List report');
    } finally {
      setLoading(false);
    }
  }, [getFilterParams]);

  useEffect(() => {
    if (configsLoaded) fetchData();
  }, [fetchData, configsLoaded]);

  const { sortConfig, requestSort, sortItems } = useSort('opened_date', 'desc');

  const handleDateChange = ({ start_date, end_date }) => {
    setStartDate(start_date);
    setEndDate(end_date);
  };

  const getExportParams = () => ({
    ...getFilterParams(),
    columns: visibleColumns.join(','),
  });

  const handleExportCSV = () => {
    reportService.downloadCSV(reportService.getCaseList, getExportParams(), 'case_list_report.csv');
  };

  const handleExportPDF = () => {
    reportService.downloadPDF(reportService.getCaseList, getExportParams(), 'case_list_report.pdf');
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-64 bg-gray-100 rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto h-8 w-8 text-red-400 mb-2" />
        <p className="text-gray-500">{error}</p>
        <button onClick={fetchData} className="mt-2 text-yellow-600 hover:text-yellow-800 text-sm">Retry</button>
      </div>
    );
  }

  const { summary, items } = data || { summary: {}, items: [] };
  const sortedItems = sortItems(items);
  const activeConfig = savedConfigs.find(c => c.id === activeConfigId);

  const selectClass = "px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500";

  return (
    <div className="space-y-6">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Case List</h2>
        <div className="flex items-center gap-2">
          {/* Saved Configs Dropdown */}
          <div className="relative" ref={configDropdownRef}>
            <button
              onClick={() => setShowConfigDropdown(!showConfigDropdown)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <span className="truncate max-w-[140px]">{activeConfig ? activeConfig.name : 'Default View'}</span>
              <ChevronDown className="h-4 w-4 flex-shrink-0" />
            </button>

            {showConfigDropdown && (
              <div className="absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-30">
                <div className="py-1">
                  <button
                    onClick={handleSelectDefault}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${!activeConfigId ? 'font-semibold text-yellow-700 bg-yellow-50' : 'text-gray-700'}`}
                  >
                    Default View
                  </button>
                  {savedConfigs.length > 0 && <div className="border-t my-1" />}
                  {savedConfigs.map(config => (
                    <div key={config.id} className={`flex items-center justify-between px-4 py-2 hover:bg-gray-50 ${config.id === activeConfigId ? 'bg-yellow-50' : ''}`}>
                      <button
                        onClick={() => handleSelectConfig(config)}
                        className={`flex-1 text-left text-sm truncate ${config.id === activeConfigId ? 'font-semibold text-yellow-700' : 'text-gray-700'}`}
                      >
                        {config.name}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteConfig(config.id); }}
                        className="p-1 text-gray-400 hover:text-red-500 rounded"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Save button */}
          <button
            onClick={() => {
              if (activeConfigId) {
                // Quick-save existing config
                handleSaveConfig();
              } else {
                setShowSaveDialog(true);
              }
            }}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            title={activeConfigId ? `Save "${activeConfig?.name}"` : 'Save view...'}
          >
            <Save className="h-4 w-4" />
            {activeConfigId ? 'Save' : 'Save View'}
          </button>

          {/* Save As (only when editing existing config) */}
          {activeConfigId && (
            <button
              onClick={() => { setSaveConfigName(''); setShowSaveDialog(true); }}
              className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              title="Save as new view"
            >
              Save As
            </button>
          )}

          {/* Columns button */}
          <button
            onClick={() => setShowColumnPicker(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <SlidersHorizontal className="h-4 w-4" /> Columns
          </button>

          <button onClick={handleExportCSV} className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            <Download className="h-4 w-4" /> Export CSV
          </button>
          <button onClick={handleExportPDF} className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            <FileText className="h-4 w-4" /> Export PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <DateRangeFilter startDate={startDate} endDate={endDate} onChange={handleDateChange} />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {selectedAttorneys.map(a => (
            <span key={a.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
              {a.display_name}
              <button type="button" onClick={() => setSelectedAttorneys(prev => prev.filter(x => x.id !== a.id))} className="hover:text-yellow-600">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <UserSelectDropdown
            value={null}
            onChange={(userId, user) => {
              if (userId && user && !selectedAttorneys.some(a => a.id === userId)) {
                setSelectedAttorneys(prev => [...prev, { id: userId, display_name: user.display_name }]);
              }
            }}
            placeholder={selectedAttorneys.length ? 'Add attorney...' : 'All Attorneys'}
            className="w-52"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selectClass}>
          {STATUS_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select value={caseTypeFilter} onChange={(e) => setCaseTypeFilter(e.target.value)} className={selectClass}>
          <option value="">All Types</option>
          {caseTypes.map(t => (
            <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
          ))}
        </select>
      </div>

      {/* Summary Card */}
      <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
        <div className="bg-blue-50 rounded-lg p-4 w-fit">
          <p className="text-xs text-gray-500">Total Cases</p>
          <p className="text-lg font-bold text-blue-700">{summary.total_cases || 0}</p>
        </div>
      </div>

      {/* Dynamic Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {visibleColumns.map(key => {
                  const col = ALL_COLUMNS.find(c => c.key === key);
                  return (
                    <SortableHeader
                      key={key}
                      label={col?.label || key}
                      sortKey={key}
                      sortConfig={sortConfig}
                      onSort={requestSort}
                    />
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedItems.length === 0 ? (
                <tr><td colSpan={visibleColumns.length} className="px-4 py-8 text-center text-gray-400">No cases found</td></tr>
              ) : sortedItems.map((item) => (
                <tr key={item.case_id} className="hover:bg-gray-50">
                  {visibleColumns.map(key => (
                    <td key={key} className="px-4 py-3 text-sm">
                      {renderCell(key, item)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Column Picker Modal */}
      {showColumnPicker && (
        <ColumnPicker
          allColumns={ALL_COLUMNS}
          visibleColumns={visibleColumns}
          defaultColumns={DEFAULT_COLUMNS}
          onApply={setVisibleColumns}
          onClose={() => setShowColumnPicker(false)}
        />
      )}

      {/* Save Config Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Save View</h3>
            <input
              type="text"
              value={saveConfigName}
              onChange={(e) => setSaveConfigName(e.target.value)}
              placeholder="e.g. Active Cases by Attorney"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && saveConfigName.trim()) {
                  handleSaveAsConfig(saveConfigName.trim());
                }
              }}
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => { setShowSaveDialog(false); setSaveConfigName(''); }}
                className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSaveAsConfig(saveConfigName.trim())}
                disabled={!saveConfigName.trim()}
                className="px-4 py-1.5 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseListReport;
